import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";

import { useUserSheet } from "./userSheetContext";

import { Column, Row } from "@/components";

import { cn } from "@/lib/utils";

const MAX_COMMS_WIDTH_PERCENT = 45;
const MIN_COMMS_WIDTH_PERCENT = 20;

// The sheet body row: the main column (header + tabs, or a takeover) beside
// the resizable communications rail. The rail's panel mounts lazily so an
// unopened rail costs no comms queries.
export const UserSheetBody = ({
  children,
  commsPanel,
}: {
  children: ReactNode;
  commsPanel?: ReactNode;
}) => {
  /* React Hooks */
  const {
    communicationsWidthPercent,
    hasCommunications,
    isCommunicationsOpen,
    setCommunicationsWidthPercent,
  } = useUserSheet();
  const [isResizingCommunications, setIsResizingCommunications] =
    useState(false);
  // Sticks once opened: the panel keeps its drafts across a collapse, but a
  // rail that's never been opened costs no comms queries at all.
  const [hasOpenedCommunications, setHasOpenedCommunications] = useState(
    isCommunicationsOpen,
  );
  const sheetBodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isCommunicationsOpen) setHasOpenedCommunications(true);
  }, [isCommunicationsOpen]);

  useEffect(() => {
    if (!isResizingCommunications) return;

    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };
  }, [isResizingCommunications]);

  /* Handlers */
  const handleCommsResize = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!isResizingCommunications) return;
    const sheetBody = sheetBodyRef.current;
    if (!sheetBody) return;

    const bounds = sheetBody.getBoundingClientRect();
    const widthPercent = ((bounds.right - event.clientX) / bounds.width) * 100;
    setCommunicationsWidthPercent(
      Math.min(
        MAX_COMMS_WIDTH_PERCENT,
        Math.max(MIN_COMMS_WIDTH_PERCENT, widthPercent),
      ),
    );
  };

  /* Render */
  const renderCommsRail = () => {
    if (!hasCommunications || commsPanel === undefined) return null;

    return (
      <Column
        className={cn(
          "relative min-h-0 shrink-0 overflow-visible",
          !isResizingCommunications &&
            "transition-[width] duration-500 ease-out",
          !isCommunicationsOpen && "pointer-events-none w-0",
        )}
        style={{
          width: isCommunicationsOpen
            ? `${communicationsWidthPercent.toString()}%`
            : undefined,
        }}
      >
        <Row
          className="absolute bottom-4 left-3 top-4 z-20 w-2 cursor-col-resize"
          onPointerDown={(event) => {
            event.currentTarget.setPointerCapture(event.pointerId);
            setIsResizingCommunications(true);
          }}
          onPointerCancel={() => {
            setIsResizingCommunications(false);
          }}
          onPointerMove={handleCommsResize}
          onPointerUp={(event) => {
            event.currentTarget.releasePointerCapture(event.pointerId);
            setIsResizingCommunications(false);
          }}
        />
        <Column
          className={cn(
            "h-full min-h-0 min-w-0 overflow-hidden transition-[opacity,transform] duration-500 ease-out",
            isCommunicationsOpen
              ? "translate-x-0 opacity-100"
              : "translate-x-8 opacity-0",
          )}
        >
          {hasOpenedCommunications ? commsPanel : null}
        </Column>
      </Column>
    );
  };

  const render = () => {
    return (
      <Row
        className="min-h-0 min-w-0 flex-1 overflow-hidden"
        ref={sheetBodyRef}
      >
        <Column className="min-h-0 min-w-0 flex-1">{children}</Column>
        {renderCommsRail()}
      </Row>
    );
  };

  return render();
};
